import { type ChangeEvent, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

import type { LineItem } from "~/features/cart/cart/models/CartData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

import {
  deletePackageEntry,
  selectPackaging,
  toggleCommentVisibility,
  toggleShowPackaging,
  updateComment,
  updateDate,
  updatePoNumber,
  updatePriceReference,
  updateQuantity,
  updateSalesEnabled,
} from "~/features/cart/cart/redux/actions/cartPageActions";
import { clearManualDate, updateCartTargetPrice } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import { cartIsDayDisabled } from "~/common/utils/dateUtils";

export interface UseLineItemActionsProps {
  lineItem: LineItem;
  product: ProductDescriptor;
}

export interface LineItemActions {
  /** Check if a day is disabled in the date picker */
  isDayDisabled: (date: Date) => boolean;
  /** Update the requested delivery date */
  updateDate: (date: Date) => void;
  /** Clear manually set date, reverting to estimated delivery date */
  clearDate: () => void;
  /** Update the quantity */
  updateQty: (quantity: number) => void;
  /** Update the comment text */
  updateComment: (comment: string) => void;
  /** Toggle comment section visibility */
  toggleShowComment: () => void;
  /** Update the purchase order number */
  updatePONumber: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  /** Update the price reference */
  updatePriceReference: (priceRef: string | undefined) => void;
  /** Update the target price */
  updateCartTargetPrice: (price: PriceData | undefined) => void;
  /** Delete this line item from cart */
  removeEntry: () => void;
  /** Toggle packaging selection panel visibility */
  toggleShowPackaging: () => void;
  /** Open packaging selection panel */
  onPackagingClick: () => void;
  /** Close packaging selection panel */
  closePackaging: () => void;
  /** Select a package code */
  selectPackage: (packCode: string) => void;
  /** Enable/disable sales campaign for this line item */
  updateSalesEnabled: (enabled: boolean) => void;
}

/**
 * Hook that provides all action handlers for a line item.
 * Uses refs to avoid stale closures and ensure actions always use current data.
 *
 * @param props - Line item and product descriptor
 * @returns Object with all line item action handlers
 */
export function useLineItemActions({ lineItem, product }: UseLineItemActionsProps): LineItemActions {
  const dispatch = useDispatch();

  // Use refs to avoid stale closures in memoized callbacks
  const dataRef = useRef({ lineItem, product });
  dataRef.current = { lineItem, product };

  const actions = useMemo((): LineItemActions => {
    const getLineItemId = () => dataRef.current.lineItem.id;

    return {
      isDayDisabled: (date: Date) => cartIsDayDisabled(dataRef.current.lineItem, date),

      updateDate: (date: Date) => dispatch(updateDate(date, getLineItemId())),

      clearDate: () => dispatch(clearManualDate(getLineItemId())),

      updateQty: (quantity: number) => {
        if (quantity !== undefined && quantity !== null) {
          dispatch(updateQuantity(quantity, getLineItemId()));
        }
      },

      updateComment: (comment: string) => dispatch(updateComment(comment, getLineItemId())),

      toggleShowComment: () => dispatch(toggleCommentVisibility(getLineItemId())),

      updatePONumber: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        dispatch(updatePoNumber(e.target.value, getLineItemId())),

      updatePriceReference: (priceRef: string | undefined) =>
        dispatch(updatePriceReference(priceRef || "", getLineItemId())),

      updateCartTargetPrice: (targetPrice: PriceData | undefined) => {
        // Always dispatch so bulk API is called even when clearing value; undefined will omit the attribute
        dispatch(updateCartTargetPrice(targetPrice, getLineItemId()));
      },

      removeEntry: () => dispatch(deletePackageEntry(getLineItemId())),

      toggleShowPackaging: () => dispatch(toggleShowPackaging(getLineItemId())),

      onPackagingClick: () => dispatch(toggleShowPackaging(getLineItemId())),

      closePackaging: () => dispatch(toggleShowPackaging(getLineItemId(), false)),

      selectPackage: (packCode: string) => dispatch(selectPackaging(getLineItemId(), packCode)),

      updateSalesEnabled: (enabled: boolean) => dispatch(updateSalesEnabled(enabled, getLineItemId())),
    };
  }, [dispatch]);

  return actions;
}

/**
 * Lower-level hook that exposes the dispatch and dataRef for more advanced use cases.
 * Used when you need to extend the base actions with additional custom actions.
 *
 * @param lineItem - The line item
 * @param product - The product descriptor
 * @returns Object with base actions, dataRef, and dispatch
 */
export function useAbstractLineItemActions(lineItem: LineItem, product: ProductDescriptor) {
  const dataRef = useRef({ lineItem, product });
  dataRef.current = { lineItem, product };

  const dispatch = useDispatch();

  const actions = useMemo(
    () => ({
      isDayDisabled: (date: Date) => cartIsDayDisabled(dataRef.current.lineItem, date),
      updateDate: (date: Date) => dispatch(updateDate(date, dataRef.current.lineItem.id)),
      clearDate: () => dispatch(clearManualDate(dataRef.current.lineItem.id)),

      updateQty: (quantity: number) => {
        if (quantity !== undefined && quantity !== null) {
          dispatch(updateQuantity(quantity, dataRef.current.lineItem.id));
        }
      },
      updateComment: (comment: string) => dispatch(updateComment(comment, dataRef.current.lineItem.id)),
      updatePriceReference: (priceRef: string | undefined) =>
        dispatch(updatePriceReference(priceRef || "", dataRef.current.lineItem.id)),
      updatePONumber: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        dispatch(updatePoNumber(e.target.value, dataRef.current.lineItem.id)),
      removeEntry: () => dispatch(deletePackageEntry(dataRef.current.lineItem.id)),
      toggleShowComment: () => dispatch(toggleCommentVisibility(dataRef.current.lineItem.id)),
    }),
    [dispatch],
  );

  return {
    actions,
    dataRef,
    dispatch,
  };
}
